import { call, delay, put, race, take, takeLatest } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import { incrementMetric } from '../../../../Services/Datadog';
import {
  setBookingConfirmable,
  setLeadStatus,
} from '../../slices/clientApiJourney';
import getLeadStatusQuery from './getLeadStatusQuery';

const POLL_INTERVAL_SECONDS = 5;

// fastly limit = 30s
const REQUEST_TIMEOUT_SECONDS = 20;

const CREATED_STATUS = 'CREATED';
const FINISHED_STATUS = 'FINISHED';
const TIMEOUT_OUTCOME = 'TIMEOUT';

export const TERMINAL_STATUSES = ['FAILED', 'ABANDONED'];

const TOKEN_EXPIRED_CODE = 'TOKEN_EXPIRED';
const INVALID_TOKEN_CODE = 'INVALID_TOKEN';
const AUTH_HEADER_INVALID_TYPE_CODE = 'AUTH_HEADER_INVALID_TYPE';

const AUTH_ERROR_CODES = [
  TOKEN_EXPIRED_CODE,
  INVALID_TOKEN_CODE,
  AUTH_HEADER_INVALID_TYPE_CODE,
];

const getAuthErrorCode = (error) =>
  error?.graphQLErrors
    ?.map(({ extensions }) => extensions?.code)
    .find((code) => AUTH_ERROR_CODES.includes(code));

const isCreatedStatus = (leadStatus) => leadStatus?.status === CREATED_STATUS;

const isTerminalStatus = (leadStatus) =>
  TERMINAL_STATUSES.includes(leadStatus?.status);

export const isTerminalCutoff = (leadStatus) =>
  leadStatus?.status === FINISHED_STATUS &&
  leadStatus?.outcome === TIMEOUT_OUTCOME;

export function* getLeadStatusWorker({ payload }) {
  const { submissionId, token } = payload;

  let isBookingConfirmable = false;
  yield put(setBookingConfirmable({ isBookingConfirmable }));

  while (true) {
    yield delay(POLL_INTERVAL_SECONDS * 1000);

    try {
      const { leadStatus, timedOut } = yield race({
        leadStatus: call(getLeadStatusQuery, {
          token,
          variables: { submissionId },
        }),
        timedOut: delay(REQUEST_TIMEOUT_SECONDS * 1000),
      });

      if (!timedOut) {
        yield put(setLeadStatus({ leadStatus }));

        if (isCreatedStatus(leadStatus) && !isBookingConfirmable) {
          isBookingConfirmable = true;
          yield put(setBookingConfirmable({ isBookingConfirmable }));
        }

        if (isTerminalStatus(leadStatus) || isTerminalCutoff(leadStatus)) {
          if (isBookingConfirmable) {
            yield put(setBookingConfirmable({ isBookingConfirmable: false }));
          }
          return;
        }
      }
    } catch (error) {
      const authErrorCode = getAuthErrorCode(error);

      if (authErrorCode) {
        incrementMetric({
          name: 'getLeadStatusError',
          tags: {
            type:
              authErrorCode === TOKEN_EXPIRED_CODE ? 'token_expired' : 'auth',
          },
        });

        if (isBookingConfirmable) {
          yield put(setBookingConfirmable({ isBookingConfirmable: false }));
        }
        return;
      }

      incrementMetric({
        name: 'getLeadStatusError',
        tags: { type: 'exception' },
      });
    }
  }
}

export function* pollLeadStatusUntilStopped(action) {
  yield race({
    task: call(getLeadStatusWorker, action),
    stopped: take(ActionTypes.STOP_LEAD_STATUS_POLL),
  });
}

export default function* getLeadStatusWatcher() {
  yield takeLatest(ActionTypes.GET_LEAD_STATUS, pollLeadStatusUntilStopped);
}
